import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from '@/lib/analytics';
import {
  ArrowRight,
  CheckCircle2,
  HeartPulse,
  Mail,
  AlertTriangle,
  ShieldCheck,
  Globe,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Blinded case study — a major Australian health service (Barwon Health),
// not yet named publicly. Honest framing: this is a proof of concept of the
// AI email exception-prevention layer on Oracle Fusion. Only real, sourced
// POC figures are used (no fabricated ROI). Un-blind on customer all-clear.
const CustomerHealthSystem = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:
      'How a major Australian health service piloted AI-driven AP exception prevention on Oracle Fusion Cloud ERP',
    description:
      "An Australian public health service ran a proof of concept of EZ Cloud's AI email exception prevention on Oracle Fusion Cloud ERP, replacing manual AP triage. The POC processed 2,500+ supplier emails with zero errors.",
    author: { '@type': 'Organization', name: 'EZ Cloud' },
    publisher: {
      '@type': 'Organization',
      name: 'EZ Cloud',
      logo: { '@type': 'ImageObject', url: 'https://ezcloud.ai/ezcloud-logo-v2.png' },
    },
    datePublished: '2026-05-28',
    mainEntityOfPage: 'https://ezcloud.ai/customers/health-system-oracle-fusion',
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Health System on Oracle Fusion — AP Automation Case Study | EZ Cloud"
        description="How a major Australian health service piloted EZ Cloud's AI email exception prevention on Oracle Fusion Cloud ERP, replacing manual AP triage — 2,500+ supplier emails processed with zero errors."
        canonical="https://ezcloud.ai/customers/health-system-oracle-fusion"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Customers', url: 'https://ezcloud.ai/customers' },
          { name: 'Health system on Oracle Fusion', url: 'https://ezcloud.ai/customers/health-system-oracle-fusion' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <HeartPulse className="w-4 h-4" aria-hidden />
              Case Study · Healthcare · Oracle Fusion Cloud ERP
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              A major Australian health service
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl">
              Replacing manual AP triage with AI email exception prevention — proven in a
              proof of concept on Oracle Fusion Cloud ERP.
            </p>

            {/* POC headline figures — sourced, no fabricated ROI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {[
                { value: '2,500+', label: 'Supplier emails processed', icon: Mail },
                { value: '0', label: 'Processing errors', icon: ShieldCheck },
                { value: '100%', label: 'Routing validated end-to-end', icon: CheckCircle2 },
                { value: '15', label: 'Invoice scenarios covered', icon: Globe },
              ].map(({ value, label, icon: Icon }) => (
                <div key={label} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                  <Icon className="w-5 h-5 text-accent mb-2" aria-hidden />
                  <div className="text-3xl font-bold text-accent mb-1">{value}</div>
                  <div className="text-sm text-white/70">{label}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/50 mt-4">
              Figures from the formal proof-of-concept test run, not a production benchmark.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-4 max-w-4xl py-16 space-y-12">
          {/* At a glance */}
          <section aria-labelledby="glance">
            <h2 id="glance" className="text-2xl font-semibold text-gray-900 mb-4">
              At a glance
            </h2>
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div>
                  <dt className="text-gray-500">Industry</dt>
                  <dd className="text-gray-900 font-medium">Public healthcare (Australia)</dd>
                </div>
                <div>
                  <dt className="text-gray-500">ERP</dt>
                  <dd className="text-gray-900 font-medium">Oracle Fusion Cloud ERP (via Oracle Integration Cloud)</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Before EZ Cloud</dt>
                  <dd className="text-gray-900 font-medium">Manual AP processing and email triage</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Engagement</dt>
                  <dd className="text-gray-900 font-medium">Proof of concept — AI email exception prevention</dd>
                </div>
              </dl>
            </Card>
          </section>

          {/* Challenge */}
          <section aria-labelledby="challenge">
            <h2 id="challenge" className="text-2xl font-semibold text-gray-900 mb-4">
              The challenge
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Like most health services, the organization ran accounts payable manually. Before an
              invoice ever reached a workflow, the AP team worked through a high volume of inbound
              supplier email by hand — and the invoices themselves spanned a wide range of clinical
              and corporate scenarios. The team needed to prove that automation could absorb this
              upstream work on top of Oracle Fusion, using Oracle-supported integration, without
              disrupting a live finance environment.
            </p>
            <ul className="space-y-3">
              {[
                'Manual triage of supplier email — payment-status chasing, missing PO numbers, duplicate submissions, statements, and urgent requests',
                'A wide range of invoice scenarios: PO and non-PO, credit notes, already-paid, not-yet-receipted, proforma, reimbursements',
                'A requirement to integrate via Oracle-supported methods, not a brittle custom connector',
                'Healthcare data sensitivity and Australian data-residency expectations',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-gray-600">
                  <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Solution */}
          <section aria-labelledby="solution">
            <h2 id="solution" className="text-2xl font-semibold text-gray-900 mb-4">
              The solution
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              EZ Cloud layered its AI email exception prevention over Oracle Fusion, connected through
              Oracle Integration Cloud. Inbound supplier email is read, classified, and routed
              automatically; structured invoices flow through capture, duplicate and active-vendor
              checks, and three-way match before posting to Fusion via Oracle-supported methods.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  title: 'AI email exception prevention',
                  body: 'Supplier email read via Microsoft Graph, classified and routed automatically — the manual triage step, handled before invoices enter the workflow.',
                },
                {
                  title: 'Oracle Fusion via OIC',
                  body: 'Integration through Oracle Integration Cloud and Oracle-supported methods — no custom base-table writes, no brittle connector.',
                },
                {
                  title: '15 invoice scenarios',
                  body: 'PO and non-PO, credit notes, already-paid, not-receipted, statements, proforma, reimbursements, and urgent invoices all modelled.',
                },
                {
                  title: 'Enterprise security',
                  body: 'Azure AD single sign-on, role-based access, and a SOC 2 Type II-certified environment — independently security-reviewed for a healthcare deployment.',
                },
              ].map((item) => (
                <Card key={item.title} className="p-5 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Results */}
          <section aria-labelledby="results">
            <h2 id="results" className="text-2xl font-semibold text-gray-900 mb-6">
              What the proof of concept showed
            </h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  2,500+ supplier emails processed with zero errors
                </h3>
                <p>
                  In the formal POC test run, EZ Cloud's AI processed more than 2,500 supplier emails
                  end-to-end without a single processing error — the upstream triage that had been
                  done manually, handled automatically.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  Routing validated 100% end-to-end
                </h3>
                <p>
                  Every routing action was confirmed programmatically via Microsoft Graph, giving the
                  team independent verification that emails reached the right destination.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  The health service's full scenario range, covered
                </h3>
                <p>
                  The POC exercised the organization's 15 invoice scenarios — from straightforward PO
                  invoices to credit notes, proforma, reimbursements, and urgent requests — on top of
                  Oracle Fusion, without disrupting the live environment.
                </p>
              </div>
            </div>
            <Card className="p-5 bg-gray-50 border border-gray-200 mt-8">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">Where it stands:</span> this engagement
                is a proof of concept; the figures above are from the formal POC test run rather than a
                production operating benchmark. We'll publish production outcomes as the deployment
                matures.
              </p>
            </Card>
          </section>

          {/* Related */}
          <section aria-labelledby="related" className="border-t border-gray-200 pt-10">
            <h2 id="related" className="text-2xl font-semibold text-gray-900 mb-6">
              Related EZ Cloud resources
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/solutions/healthcare-oracle-fusion"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Healthcare AP on Oracle Fusion
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  The full solution for health systems running Oracle Fusion Cloud ERP — compliance, OCI residency, AI email prevention.
                </p>
              </Link>
              <Link
                to="/integrations/oracle"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Oracle EBS &amp; Fusion integration
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  How EZ Cloud posts into Oracle Fusion and EBS using Oracle-supported integration methods.
                </p>
              </Link>
              <Link
                to="/technology/ai-email-prevention"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  AI email prevention
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  The exception-prevention layer that resolves supplier email before invoices enter the workflow.
                </p>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <section className="border-t border-gray-200 pt-12 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              Run Oracle Fusion? See it against your AP.
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Book a 30-minute walkthrough and we'll show AP automation and AI email exception
              prevention on Oracle Fusion Cloud ERP — against your invoice sample.
            </p>
            <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
              Book an Oracle AP demo
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Button>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CustomerHealthSystem;
